declare const window: any;

import bugsnag, { Bugsnag } from "@bugsnag/js";
import { scrubPath } from "./telemetry";

const config = window && window.initProps && window.initProps.config ? window.initProps.config : {};
const devLogger = {
  ...console,
  warn: () => {
    return;
  }
};

export const bugsnagClient = bugsnag({
  apiKey: "8ec14a2967355486bfc76a1c351a7dc8",
  appVersion: config.commit,
  autoBreadcrumbs: false,
  networkBreadcrumbsEnabled: true,
  collectUserIp: false,
  releaseStage: config.env,
  notifyReleaseStages: ["integration", "staging", "production"],
  logger: process.env.NODE_ENV === "development" ? devLogger : console,
  beforeSend: (report: Bugsnag.Report) => {
    // Reports triggered by UI tests are not useful. TestCafe removes source maps.
    // Reports without a stacktrace are not actionable.
    // Reports originating from global code are not actionable. This code is out of our control.
    // Typically happening when the current web view is extended with custom code that has bugs.
    if (isTest() || !hasStacktrace(report) || isGlobalCode(report)) {
      report.ignore();
    }

    // Path without query, used to group errors in Bugsnag portal
    report.context = scrubPath(report.context);

    // Full URL, displayed as metadata
    report.request.url = scrubPath(report.request.url);

    report.breadcrumbs.forEach((breadcrumb: Bugsnag.Breadcrumb) => {
      if (breadcrumb.metaData && breadcrumb.metaData["request"]) {
        breadcrumb.metaData["request"] = scrubPath(breadcrumb.metaData["request"]);
      }
    });

    if (window.initProps.user) {
      report.user = {
        id: window.initProps.user.id
      };
    }
  }
});

function isTest() {
  // This property is set by testcafe-hammerhead. It's presence indicates that the application
  // is currently executed by TestCafe.
  return !!window["%hammerhead%"];
}

/**
 * Does the report have a stacktrace?
 *
 * Browsers discard stacktrace i.e. when under memory pressure.
 * @param report Error report
 * @returns True if the report has a stacktrace with at least two frames with a line number
 */
export function hasStacktrace(report: Partial<Bugsnag.Report>): boolean {
  return !!report.stacktrace && report.stacktrace.filter((frame) => !!frame.lineNumber).length > 1;
}

/**
 * Is the error report originating from global code?
 *
 * @param report Error report
 * @returns True if the last stacktrace frame is from global code
 */
export function isGlobalCode(report: Partial<Bugsnag.Report>): boolean {
  if (!report.stacktrace || report.stacktrace.length < 1) {
    return false;
  }

  const lastFrame = report.stacktrace[report.stacktrace.length - 1];

  return lastFrame.method === "global code" || lastFrame.file === "global code";
}
